import React, { useCallback, useEffect, useState } from 'react';

import { Collapse, Skeleton } from '@mui/material';

import useDiffAsync from 'hooks/use-diff-async';
import MarkdownElement from 'main/components/utils/MarkdownElement';
import { useHistoryOfChanges } from 'pages/Bulletin/providers/history-of-changes';
import ProtectedData from 'pages/components/ProtectedData';
import Html from 'utils/Html';

import useStyles from './styles';

interface Props {
    expanded: boolean;
    short: string;
    full: string;
    className?: string;
}

const Description: React.FC<Props> = ({ short, full, expanded, className }) => {
    const { classes, cx } = useStyles();
    const { revision } = useHistoryOfChanges();

    /** replace for markdown wrong parsed Bold text */
    const refineMd = useCallback((md: string) => md.replace(/\*\*\s?(.+?)\s\*\*/g, '**$1**'), []);
    const [value, setValue] = useState(() => ({ type: 'default', text: refineMd(full ?? '') }));

    const [diff, isComputing] = useDiffAsync({ method: 'words' });

    const computeDifference = useCallback(
        async (oldValue: string, newValue: string) => {
            const changes = await diff(oldValue, newValue);
            const text = changes.reduce((acc, { value, added, removed }) => {
                switch (true) {
                    case !!added:
                        return `${acc}<span class=${classes.added}>${value}</span>`;
                    case !!removed:
                        return `${acc}<span class=${classes.removed}>${value}</span>`;
                    default:
                        return `${acc}${value}`;
                }
            }, '');
            setValue({ type: 'diff', text });
        },
        [classes.added, classes.removed, diff]
    );

    useEffect(() => {
        const newValue = refineMd(full ?? '');
        setValue({ type: 'default', text: newValue });

        if (revision && 'description' in revision.changes) {
            const oldValue = refineMd(revision.changes.description ?? '');
            if (oldValue !== newValue) {
                void computeDifference(oldValue, newValue);
            }
        }
    }, [computeDifference, full, refineMd, revision]);

    return (
        <div className={cx(classes.root, className)}>
            <p className={cx(classes.short, { [classes.pale]: expanded })}>{short}</p>
            <Collapse in={expanded} collapsedSize={0} mountOnEnter unmountOnExit>
                {isComputing ? (
                    <>
                        <Skeleton variant="text" className={classes.skeleton} />
                        <Skeleton variant="text" className={classes.skeleton} />
                        <Skeleton variant="text" className={classes.skeleton} />
                    </>
                ) : value.type === 'default' ? (
                    <ProtectedData data={full}>
                        <MarkdownElement className={classes.full} text={value.text} />
                    </ProtectedData>
                ) : value.type === 'diff' ? (
                    <ProtectedData data={full}>
                        <Html className={classes.full} html={value.text} />
                    </ProtectedData>
                ) : null}
            </Collapse>
        </div>
    );
};

export default Description;
